import { isEmpty } from 'lodash';
import { FC, useCallback, useEffect, useState } from 'react';

import { EmailContentDefinition } from 'modules/graphql/types';
import { useGetTemplateUserJsonTestData } from 'modules/templateEditor/hooks/useGetTemplateUserJsonTestData';
import { useLocalStorage } from 'utils/hooks/useLocalStorage';

import { EmailPreviewType, PreviewWithDataErrorMessage } from '../../common/types';
import {
  getContentWithData,
  getContentWithDataAndMergeParameters,
  getPreviewWithDataError,
} from '../../common/utils';
import { useEditableFieldsContext } from '../../context/EditableFieldsContext';
import { usePreviewWithDataContext } from '../../context/PreviewWithDataContext';
import { useQueryParamContext } from '../../context/QueryParamContext';
import { EmailPreviewContext, EmailPreviewContextState } from './EmailPreviewContext';

export const EmailPreviewContextProvider: FC<React.PropsWithChildren<unknown>> = ({ children }) => {
  const { liveTemplate } = useEditableFieldsContext();
  const { canRefreshPreview, dataFeedData, userData, isUserDataDirty, setIsUserDataDirty } =
    usePreviewWithDataContext();

  const { templateId } = useQueryParamContext();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [messageContent, setMessageContent] = useState<string>('');
  const [messageFromName, setMessageFromName] = useState<string>('');
  const [messagePreheader, setMessagePreheader] = useState<string>('');
  const [messageSubject, setMessageSubject] = useState<string>('');
  const [selectedPreview, setSelectedPreview] = useLocalStorage<EmailPreviewType>(
    `${templateId}-emailPreview`,
    EmailPreviewType.Desktop,
  );
  const [previewError, setPreviewError] = useState<PreviewWithDataErrorMessage | undefined>(
    undefined,
  );
  const emailData = (liveTemplate?.mediumData || {}) as EmailContentDefinition;
  const locale = emailData?.locale?.name || '';
  const hasContent = !isEmpty(emailData);
  const subject = emailData.subject ?? '';
  const webTitle = emailData.preheaderText;
  const webBody = emailData.fromName; // api call does not support fromName, so using unused parameter

  const { getTemplateUserJsonTestData } = useGetTemplateUserJsonTestData();

  const onRefreshPreview = useCallback(async () => {
    // Only make request when template data has been received
    if (canRefreshPreview && hasContent) {
      const userDataToSave = await getTemplateUserJsonTestData(userData, isUserDataDirty);

      setIsLoading(true);
      getContentWithData(
        templateId,
        {
          subject,
          webBody,
          webTitle,
        },
        JSON.parse(dataFeedData),
        JSON.parse(userDataToSave),
      )
        .then(({ data }) => {
          setIsUserDataDirty(false);
          setMessagePreheader(data.webTitle);
          setMessageSubject(data.subject);
          setMessageFromName(data.webBody);
        })
        .catch(() => {
          /* preview error doesn't actually get returned at this endpoint for this medium */
        })
        .finally(() => {
          getContentWithDataAndMergeParameters(templateId, locale)
            .then(({ data }) => {
              setMessageContent(data);
              setPreviewError(undefined);
            })
            .catch(({ response }) => {
              setPreviewError(getPreviewWithDataError(response?.data?.message || ''));
            })
            .finally(() => setIsLoading(false));
        });
    }
  }, [
    canRefreshPreview,
    dataFeedData,
    hasContent,
    locale,
    subject,
    templateId,
    userData,
    webBody,
    webTitle,
    isUserDataDirty,
    setIsUserDataDirty,
    getTemplateUserJsonTestData,
  ]);

  /* Note: we only want this useEffect to run when dataFeedData, userData, or locale changes even though
   * it is also dependent on onRefreshPreview. However, onRefreshPreview shares ALL of the useEffect's
   * dependencies, so the version of onRefreshPreview used will always be current.
   * Including onRefreshPreview as a dependency here would cause an unintended side-effect of
   * running onRefreshPreview on each change of fullSmsMessage */
  useEffect(() => {
    if (canRefreshPreview) {
      onRefreshPreview();
    }
  }, [canRefreshPreview, dataFeedData, userData, locale]);

  const context: EmailPreviewContextState = {
    isLoading,
    messageContent,
    messageFromName,
    messagePreheader,
    messageSubject,
    onRefreshPreview,
    previewError,
    selectedPreview,
    setSelectedPreview,
  };

  return <EmailPreviewContext.Provider value={context}>{children}</EmailPreviewContext.Provider>;
};
